import { jsx as _jsx } from "react/jsx-runtime";
import { CanvasTabs } from '#pplx/frontend/apps/ask/canvas-shared/state/canvasReducer.js';
import { CanvasContext } from '#pplx/frontend/apps/ask/canvas-shared/state/useCanvasContext.js';
import { getAssetTitle } from '#pplx/frontend/apps/ask/canvas-shared/useAssetInfo.js';
import { useSideContent } from '#pplx/frontend/apps/ask/search-side-content/core/ProviderShared.js';
import { useNavigationGuards } from '#pplx/frontend/router/navigationGuard.js';
import { useRouter } from '#pplx/frontend/router/provider.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import { useSearchParams } from '#pplx/frontend/router/useSearchParams.js';
import React, { useCallback, useMemo, useRef, useState } from 'react';
export const CanvasStateProvider = React.memo(function CanvasStateProvider({ children }) {
    const { tab, dispatch } = useSideContent();
    const latestCanvasDispatch = useRef(undefined);
    const [editAttachments, setEditAttachments] = useState();
    const [editComposers, setEditComposers] = useState([]);
    const [canvasState, setCanvasState] = useState();
    const { requestNavigation } = useNavigationGuards();
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const pathnameRef = useRef(pathname);
    const searchParamsRef = useRef(searchParams);
    // keep refs in sync with latest values
    pathnameRef.current = pathname;
    searchParamsRef.current = searchParams;
    const openCanvas = useCallback(() => {
        dispatch({ action: 'open', tabType: 'canvas' });
        const newQuery = new URLSearchParams(searchParamsRef.current?.toString());
        newQuery.set('preview', '1');
        router.replace(`${pathnameRef.current}?${newQuery.toString()}`);
    }, [router, dispatch]);
    const closeCanvas = useCallback(() => requestNavigation(() => {
        dispatch({ action: 'close' });
        latestCanvasDispatch.current = undefined;
        setCanvasState(undefined);
        const newQuery = new URLSearchParams(searchParamsRef.current?.toString());
        newQuery.delete('preview');
        router.replace(`${pathnameRef.current}?${newQuery.toString()}`);
    }), [router, dispatch, requestNavigation]);
    const pendingActions = useRef([]);
    const doCanvasAction = useCallback((action) => {
        const applyAction = () => {
            // For setAsset, also update canvasState directly so that if the viewer
            // remounts, it initializes with the correct state
            if (action.type === 'setAsset') {
                setCanvasState((prev) => ({
                    ...(prev ?? { loading: false, activeTab: CanvasTabs.Preview }),
                    assetUuid: action.assetUuid,
                    title: action.title,
                    backendUuid: action.backendUuid,
                    asset: action.asset,
                    assetContext: action.assetContext ?? prev?.assetContext,
                }));
            }
            if (latestCanvasDispatch.current) {
                latestCanvasDispatch.current(action);
            }
            else {
                pendingActions.current.push(action);
            }
        };
        if (action.type === 'setAsset')
            requestNavigation(applyAction);
        else
            applyAction();
    }, [requestNavigation]);
    const _setCanvasDispatch = useCallback((dispatch) => {
        latestCanvasDispatch.current = dispatch;
        if (pendingActions.current.length > 0 && dispatch) {
            pendingActions.current.forEach((action) => dispatch(action));
            pendingActions.current = [];
        }
    }, []);
    const createAssetClickHandler = useCallback(({ backendUuid, contextUuid, assetContext, }) => {
        return (asset) => {
            if (!asset)
                return;
            requestNavigation(() => {
                const title = getAssetTitle(asset);
                openCanvas();
                doCanvasAction({
                    type: 'setAsset',
                    assetUuid: asset.uuid ?? '',
                    title,
                    backendUuid,
                    contextUuid,
                    asset,
                    assetContext,
                });
                doCanvasAction({ type: 'setTab', tab: CanvasTabs.Preview });
            });
        };
    }, [openCanvas, doCanvasAction, requestNavigation]);
    const contextValue = useMemo(() => ({
        openCanvas,
        closeCanvas,
        isCanvasOpen: tab?.name === 'canvas',
        setCanvasDispatch: _setCanvasDispatch,
        doCanvasAction,
        canvasState,
        setCanvasState,
        editAttachments,
        editComposers,
        setEditAttachments,
        setEditComposers,
        createAssetClickHandler,
    }), [
        openCanvas,
        closeCanvas,
        _setCanvasDispatch,
        doCanvasAction,
        canvasState,
        editAttachments,
        editComposers,
        tab,
        createAssetClickHandler,
    ]);
    return _jsx(CanvasContext.Provider, { value: contextValue, children: children });
});
//# sourceMappingURL=CanvasStateProvider.js.map